import { useCallback, useEffect, useState } from 'react';
import {
  type PresenceOptimisation,
  type PresencePlatformProfile,
  type PresenceProfileResult,
  type SavedProfileSummary,
  getPlatforms,
  getTargetRoles,
  listSavedProfiles,
  recallSavedProfile,
} from './api';

/**
 * Wave-1 C1 — the Profiles (Get Found) tab's REAL data hook. Replaces
 * placeholderProfiles (lib/placeholderData.ts) as Profiles.tsx's data source;
 * every field here traces to an existing, already-shipped route the LOCKED
 * presence module already exposes (NO backend changes):
 *
 *   - targetRoles      -> GET /presence/target-roles (getTargetRoles — the
 *                         SAME read usePresencePrewarm already calls)
 *   - savedProfiles     -> GET /presence/profiles (listSavedProfiles),
 *                         newest-first. An empty array is the honest "never
 *                         generated one yet" cold state.
 *   - latestProfile     -> GET /presence/profiles/:id (recallSavedProfile) on
 *                         the newest saved row's id, so the screen can show
 *                         the REAL headline/recommendedTitles text Get Found
 *                         actually produced — the lean list row omits these
 *                         ("lean by design", see SavedProfileSummary's own
 *                         doc comment), so a genuine preview needs this one
 *                         extra call. Deliberately sequenced AFTER
 *                         listSavedProfiles resolves (it needs a real id to
 *                         recall) — null when there is nothing to recall yet,
 *                         never a second parallel guess at "the latest one."
 *   - latestOptimisation -> LANE JOB-TRUST G3: the SAME recall's own
 *                         `profile.optimisation` — the full PlatformOptimisation
 *                         blob captured at save time (or null on an older row
 *                         predating this field / an unvalidated-platform
 *                         save), so a RECALLED profile gets the SAME
 *                         OptimisationReadout detail a fresh generate gets,
 *                         never a second-class summary-only view.
 *   - platforms         -> GET /presence/platforms (getPlatforms) — this app
 *                         holds no hardcoded platform list either (mirrors
 *                         apps/web/src/pages/Presence.tsx's own "the FE never
 *                         invents a platform that wasn't served" discipline).
 *
 * Deliberately does NOT call generatePresence/prewarmPresence itself — this
 * hook is a READ of what already exists, not a trigger to create new AI
 * output on every Profiles mount (usePresencePrewarm already owns the warm;
 * the actual Generate action stays a deliberate, expensive, user-initiated
 * call, same law C1's brief states for Home).
 */

export interface ProfilesDataState {
  loading: boolean;
  targetRoles: string[];
  targetRolesError: string | null;
  savedProfiles: SavedProfileSummary[];
  savedProfilesError: string | null;
  /** The newest saved row's full generated content, or null when there is no
   *  saved profile yet, or when the recall itself failed (a failed recall
   *  never blanks the rest of the screen — the lean score/platform/date from
   *  savedProfiles[0] still renders). */
  latestProfile: PresenceProfileResult | null;
  /** LANE JOB-TRUST G3 — the SAME recall's optimisation blob, alongside
   *  latestProfile (both come from the ONE recallSavedProfile call — this is
   *  not a second fetch). Null exactly when latestProfile is null (no saved
   *  row / failed recall) OR when the saved row's own optimisation was null
   *  at save time (an unvalidated platform) — never a fabricated readout. */
  latestOptimisation: PresenceOptimisation | null;
  platforms: PresencePlatformProfile[];
}

const INITIAL_STATE: ProfilesDataState = {
  loading: true,
  targetRoles: [],
  targetRolesError: null,
  savedProfiles: [],
  savedProfilesError: null,
  latestProfile: null,
  latestOptimisation: null,
  platforms: [],
};

export function useProfilesData() {
  const [state, setState] = useState<ProfilesDataState>(INITIAL_STATE);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));

    const [rolesResult, savedResult, platformsResult] = await Promise.allSettled([
      getTargetRoles(),
      listSavedProfiles(),
      getPlatforms(),
    ]);

    const savedProfiles = savedResult.status === 'fulfilled' ? savedResult.value.profiles : [];
    const newestId = savedProfiles[0]?.id;

    // Only recall when a real saved row exists — no id means no saved
    // profile yet (the honest cold state), not something to guess at.
    let latestProfile: PresenceProfileResult | null = null;
    let latestOptimisation: PresenceOptimisation | null = null;
    if (newestId) {
      try {
        const { profile } = await recallSavedProfile(newestId);
        latestProfile = profile.profile;
        latestOptimisation = profile.optimisation;
      } catch {
        // Fail-quiet: the lean summary (score/platform/date) still renders
        // from savedProfiles[0] even if this richer recall fails.
      }
    }

    setState({
      loading: false,
      targetRoles: rolesResult.status === 'fulfilled' ? rolesResult.value : [],
      targetRolesError:
        rolesResult.status === 'rejected'
          ? rolesResult.reason instanceof Error
            ? rolesResult.reason.message
            : String(rolesResult.reason)
          : null,
      savedProfiles,
      savedProfilesError:
        savedResult.status === 'rejected'
          ? savedResult.reason instanceof Error
            ? savedResult.reason.message
            : String(savedResult.reason)
          : null,
      latestProfile,
      latestOptimisation,
      platforms: platformsResult.status === 'fulfilled' ? platformsResult.value.profiles : [],
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
